import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { enNZ } from 'date-fns/locale';

interface LastVisitDateFilterProps {
  onChange: (fromDate: Date | null, toDate: Date | null) => void;
  fromDate: Date | null;
  toDate: Date | null;
}

const LastVisitDateFilter: React.FC<LastVisitDateFilterProps> = ({
  onChange,
  fromDate = null,
  toDate = null
}) => {
  // Default start date to 28 days ago if not set
  const defaultStartDate = () => subDays(new Date(), 28);
  const today = new Date();

  // Initialize with default values if not provided
  useEffect(() => {
    if (fromDate === null && toDate === null) {
      onChange(defaultStartDate(), today);
    }
  }, []);

  const handleFromDateChange = (date: Date | undefined) => {
    onChange(date || null, toDate);
  };

  const handleToDateChange = (date: Date | undefined) => {
    onChange(fromDate, date || null);
  };

  return (
    <div className="space-y-3">
      <Label className="text-base">Last Visit Date</Label>
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="space-y-2 flex-1">
          <Label htmlFor="from-date">From</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="from-date"
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {fromDate ? format(fromDate, 'PPP', { locale: enNZ }) : 'Select date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={fromDate || undefined}
                onSelect={handleFromDateChange}
                disabled={(date) => date > today || (toDate !== null && date > toDate)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2 flex-1">
          <Label htmlFor="to-date">To</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="to-date"
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {toDate ? format(toDate, 'PPP', { locale: enNZ }) : 'Select date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={toDate || undefined}
                onSelect={handleToDateChange}
                disabled={(date) => 
                  date > today || (fromDate !== null && date < fromDate)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default LastVisitDateFilter;